import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { AcceptanceCheck, LoopDetail, LoopIteration, LoopListItem, ProfileSummary, SessionSummary } from "../types";
import { Chat } from "./Chat";
import { ChevronLeftIcon, MenuIcon, PlusIcon } from "../icons";
import { useIsNarrow } from "../viewport";
import { listGlance, when, type ListGlance } from "./loopGlance";
import { LoopPage, pageTitle, type SubPage } from "./LoopPages";

const POLL_MS = 3000;

const GROUPS: Array<[ListGlance["group"], string]> = [
	["act", "待处理"],
	["run", "运行中"],
	["done", "已结束"],
];

/**
 * The loops page: the list says where each loop stands and groups the ones that wait for the person first; a loop
 * says where it stands now and what happened to it newest first, with each step's details a page down. A wide
 * screen shows the list beside the loop and its pages; a phone shows the list as a page and the loop and each page
 * under it as pages over it, each a history entry the back gesture leaves.
 */
export function Loops({ profiles, openId, onMenu, onOpenDeploy }: { profiles: ProfileSummary[]; openId?: string | null; onMenu: () => void; onOpenDeploy: () => void }) {
	const narrow = useIsNarrow();
	const [loops, setLoops] = useState<LoopListItem[]>([]);
	const [currentId, setCurrentId] = useState<string | null>(openId ?? null);
	const [pages, setPages] = useState<SubPage[]>([]);
	const [detail, setDetail] = useState<LoopDetail | null>(null);
	const [creating, setCreating] = useState(false);
	const [chatOf, setChatOf] = useState<LoopIteration | null>(null);
	const [error, setError] = useState<string | null>(null);
	// A push about a loop waiting for review opens it here.
	useEffect(() => {
		if (!openId) return;
		setCurrentId(openId);
		setPages([]);
	}, [openId]);

	const refresh = useCallback(async () => {
		try {
			const { loops } = await api.loops();
			setLoops(loops);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "加载 loop 失败");
		}
	}, []);

	const refreshDetail = useCallback(async (id: string) => {
		try {
			const { loop } = await api.loop(id);
			setDetail(loop);
		} catch (err) {
			if (err instanceof ApiError && err.status === 404) {
				setCurrentId(null);
				setPages([]);
			}
		}
	}, []);

	// Status changes are minutes apart, so polling beats a second event stream.
	useEffect(() => {
		void refresh();
		const timer = setInterval(() => void refresh(), POLL_MS);
		return () => clearInterval(timer);
	}, [refresh]);

	useEffect(() => {
		setDetail(null);
		setChatOf(null);
		if (!currentId) return;
		void refreshDetail(currentId);
		const timer = setInterval(() => void refreshDetail(currentId), POLL_MS);
		return () => clearInterval(timer);
	}, [currentId, refreshDetail]);

	// On a phone, going back in history leaves as many pages as the entry it lands on records: the loop, then the pages under it.
	useEffect(() => {
		if (!narrow) return;
		const onPop = () => {
			const depth = (history.state as { loopPages?: unknown } | null)?.loopPages;
			const keep = typeof depth === "number" ? depth : 0;
			if (keep === 0) {
				setCurrentId(null);
				setPages([]);
			} else setPages((open) => open.slice(0, keep - 1));
		};
		addEventListener("popstate", onPop);
		return () => removeEventListener("popstate", onPop);
	}, [narrow]);

	function openLoop(id: string) {
		setPages([]);
		setCurrentId(id);
		if (narrow) history.pushState({ loopPages: 1 }, "");
	}

	function openPage(page: SubPage) {
		const next = [...pages, page];
		if (narrow) history.pushState({ loopPages: 1 + next.length }, "");
		setPages(next);
	}

	function back() {
		const depth = (currentId ? 1 : 0) + pages.length;
		if (narrow && (history.state as { loopPages?: unknown } | null)?.loopPages === depth) return history.back();
		if (pages.length > 0) setPages(pages.slice(0, -1));
		else setCurrentId(null);
	}

	async function act(fn: () => Promise<{ loop: LoopDetail }>) {
		try {
			const { loop } = await fn();
			setDetail(loop);
			await refresh();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "操作失败");
		}
	}

	async function remove(id: string) {
		if (!confirm("删除此 loop？每轮对话将归档，工作区文件保留。")) return;
		try {
			await api.deleteLoop(id);
			if (narrow) history.replaceState({ loopPages: 0 }, "");
			setCurrentId(null);
			setPages([]);
			await refresh();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "删除失败");
		}
	}

	if (chatOf?.sessionId && detail) {
		const session: SessionSummary = {
			id: chatOf.sessionId,
			profile: detail.profile,
			owner: detail.owner,
			title: `${detail.title} · 第 ${chatOf.index} 轮`,
			createdBy: "loop",
			sharedWith: [],
			createdAt: chatOf.startedAt,
			updatedAt: chatOf.finishedAt ?? chatOf.startedAt,
			archived: false,
			projectId: null,
			lastSender: null,
			model: null,
			parentId: null,
			forkEntryId: null,
			anonymous: false,
		};
		return (
			<Chat
				key={session.id}
				session={session}
				profile={profiles.find((p) => p.name === detail.profile)}
				readOnly
				onMenu={onMenu}
				onBack={() => setChatOf(null)}
				onRunEnded={() => {}}
			/>
		);
	}

	const now = Date.now();
	const top = pages.at(-1);
	const view =
		currentId && detail ? (
			<LoopPage
				loop={detail}
				page={top}
				now={now}
				onOpen={openPage}
				onAbort={() => void act(() => api.abortLoop(detail.id))}
				onResume={() => void act(() => api.resumeLoop(detail.id))}
				onReview={(verdict) => void act(() => api.reviewLoop(detail.id, verdict))}
				onDelete={() => void remove(detail.id)}
				onDeploy={onOpenDeploy}
				onChat={setChatOf}
			/>
		) : null;
	const banner = error && (
		<p className="banner error">
			{error}{" "}
			<button className="ghost" onClick={() => setError(null)}>
				关闭
			</button>
		</p>
	);
	const form = creating && (
		<NewLoop
			profiles={profiles.filter((p) => p.loop?.enabled)}
			onClose={() => setCreating(false)}
			onCreated={(loop) => {
				setCreating(false);
				openLoop(loop.id);
				void refresh();
			}}
		/>
	);

	if (narrow) {
		const depth = (currentId ? 1 : 0) + pages.length;
		const title = detail ? (top ? pageTitle(detail, top) : detail.title) : "";
		return (
			<main className="chat loops loops-page loops-phone">
				<section className="phone-page" data-page aria-label="Loops" hidden={depth > 0}>
					<header className="phone-head">
						<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
							<MenuIcon size={22} />
						</button>
						<h1>Loops</h1>
						<button className="phone-new" aria-label="新建 loop" onClick={() => setCreating(true)}>
							<PlusIcon size={22} />
						</button>
					</header>
					{banner}
					<div className="phone-scroll">
						<LoopList loops={loops} current={null} now={now} onOpen={openLoop} />
					</div>
				</section>
				{depth > 0 && (
					<section key={depth} className="phone-page phone-pushed" data-page aria-label={title}>
						<header className="phone-head">
							<button className="phone-back" aria-label="返回" onClick={back}>
								<ChevronLeftIcon size={26} />
							</button>
							<h1 />
							<span className="phone-head-side" />
						</header>
						{banner}
						<div className="phone-scroll">{view ?? <p className="hint">加载中…</p>}</div>
					</section>
				)}
				{form}
			</main>
		);
	}

	return (
		<main className="chat loops loops-page">
			<header className="chat-head page-head">
				<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
					<MenuIcon size={22} />
				</button>
				<div className="titles">
					<span className="title">Loops</span>
				</div>
				<button onClick={() => setCreating(true)}>新建 loop</button>
			</header>
			{banner}
			<div className="loops-body">
				<section className="loop-list">
					<LoopList loops={loops} current={currentId} now={now} onOpen={openLoop} />
				</section>
				<section className="loop-detail">
					{view && detail ? (
						<>
							{top && (
								<button className="loop-back" onClick={back}>
									‹ {pages.length > 1 ? pageTitle(detail, pages.at(-2)!) : detail.title}
								</button>
							)}
							{view}
						</>
					) : (
						currentId && <p className="hint">加载中…</p>
					)}
				</section>
			</div>
			{form}
		</main>
	);
}

/** The loops grouped by whether they wait for the person, run, or are done; each row says where its loop stands in one line. */
function LoopList({ loops, current, now, onOpen }: { loops: LoopListItem[]; current: string | null; now: number; onOpen: (id: string) => void }) {
	if (loops.length === 0) return <p className="hint">暂无 loop</p>;
	const rows = loops.map((loop) => ({ loop, glance: listGlance(loop, now) }));
	return (
		<div className="loop-groups">
			{GROUPS.map(([group, label]) => {
				const mine = rows.filter((r) => r.glance.group === group);
				if (mine.length === 0) return null;
				return (
					<section key={group} className="loop-group" data-group={group}>
						<h3>
							{label} · {mine.length}
						</h3>
						{mine.map(({ loop, glance }) => (
							<button key={loop.id} className={`loop-card${loop.id === current ? " current" : ""}`} onClick={() => onOpen(loop.id)}>
								<span className="loop-card-text">
									<span className="loop-title">{loop.title}</span>
									<span className={`loop-line tone-${glance.tone}`}>{glance.line}</span>
									<span className="loop-meta">
										{loop.profile}
										{loop.oneShot ? " · one-shot" : ""} · {when(loop.updatedAt, now)}
									</span>
								</span>
								<span className={`pill tone-${glance.tone}`}>{glance.pill}</span>
							</button>
						))}
					</section>
				);
			})}
		</div>
	);
}

function NewLoop({ profiles, onClose, onCreated }: { profiles: ProfileSummary[]; onClose: () => void; onCreated: (loop: LoopDetail) => void }) {
	const [profile, setProfile] = useState(profiles[0]?.name ?? "");
	const [goal, setGoal] = useState("");
	const [commands, setCommands] = useState<string[]>([""]);
	const [manual, setManual] = useState(profiles[0]?.loop.defaultAcceptance.includes("manual") ?? false);
	const [maxIterations, setMaxIterations] = useState(profiles[0]?.loop.maxIterations ?? 20);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function pick(name: string) {
		setProfile(name);
		const p = profiles.find((x) => x.name === name);
		if (p) {
			setManual(p.loop.defaultAcceptance.includes("manual"));
			setMaxIterations(p.loop.maxIterations);
		}
	}

	async function submit() {
		const acceptance: AcceptanceCheck[] = commands
			.map((c) => c.trim())
			.filter(Boolean)
			.map((run) => ({ type: "command" as const, run }));
		if (manual) acceptance.push({ type: "manual" });
		if (!goal.trim()) return setError("目标不能为空");
		if (acceptance.length === 0) return setError("至少需要一条验收命令或人工确认");
		setBusy(true);
		setError(null);
		try {
			const { loop } = await api.createLoop({ profile, goal, acceptance, maxIterations });
			onCreated(loop);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "创建失败");
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="modal" onClick={onClose}>
			<div className="modal-card loop-form" onClick={(e) => e.stopPropagation()}>
				<header>
					<h3>新建 loop</h3>
					<button className="ghost" onClick={onClose}>
						关闭
					</button>
				</header>
				{profiles.length === 0 ? (
					<p className="hint">暂无启用 loop 的 profile：在 profile.yaml 设置 `loop.enabled: true` 和 `workspace.mode: persistent`</p>
				) : (
					<div className="form">
						<label>
							Profile
							<select value={profile} onChange={(e) => pick(e.target.value)}>
								{profiles.map((p) => (
									<option key={p.name} value={p.name}>
										{p.name} — {p.description}
									</option>
								))}
							</select>
						</label>
						<label>
							目标
							<textarea value={goal} rows={6} onChange={(e) => setGoal(e.target.value)} />
						</label>
						<div className="checks">
							<span>验收命令</span>
							{commands.map((c, i) => (
								<div key={i} className="row">
									<input value={c} placeholder="cd myrepo && pytest -q" onChange={(e) => setCommands((v) => v.map((x, j) => (j === i ? e.target.value : x)))} />
									<button className="ghost" onClick={() => setCommands((v) => (v.length === 1 ? [""] : v.filter((_, j) => j !== i)))}>
										×
									</button>
								</div>
							))}
							<button className="ghost" onClick={() => setCommands((v) => [...v, ""])}>
								+ 添加
							</button>
						</div>
						<label className="inline">
							<input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} />
							需要人工确认
						</label>
						<label>
							最多迭代轮数
							<input type="number" min={1} max={100} value={maxIterations} onChange={(e) => setMaxIterations(Number(e.target.value) || 1)} />
						</label>
						{error && <p className="banner error">{error}</p>}
						<div className="row">
							<button onClick={() => void submit()} disabled={busy}>
								{busy ? "启动中…" : "启动"}
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
